import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { useValidationContext } from "../../context";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { useMeterReadingDetail, usePutAdjustReadings } from "../../hooks";
import { toast } from "@shared/ui/use-toast";
import { logEvent } from "@shared/analytics/analytics";

import { Textarea } from "@shared/ui/textarea";

interface AdjustReadingDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const AdjustReadingDialog: React.FC<AdjustReadingDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const { meterRunId, readingId, goToNextReading, validatorId } =
    useValidationContext();
  const remoteUtilityId = useRemoteUtilityId();

  const { data: detail } = useMeterReadingDetail({
    remoteUtilityId,
    meterRunId,
    meterReadingId: readingId,
    validatorId,
  });

  const [currentReading, setCurrentReading] = useState<string>("");
  const [reason, setReason] = useState<string>("");

  useEffect(() => {
    if (detail?.readingData) {
      setCurrentReading(
        detail.readingData.currentReading !== null &&
        detail.readingData.currentReading !== undefined
          ? String(detail.readingData.currentReading)
          : ""
      );
    }
  }, [detail]);

  const adjustReading = usePutAdjustReadings(readingId, meterRunId);

  const handleAdjust = () => {
    if (currentReading !== "") {
      adjustReading.mutate(
        {
          meter_reading: Number(currentReading),
          remark: reason,
          rule_status: 5,
        },
        {
          onSuccess: () => {
            toast({
              variant: "default",
              title: "Success",
              description: "Reading adjusted successfully",
            });
            logEvent("MX Reading adjusted manually", {
              readingId,
              currentReading,
              reason,
            });
            goToNextReading();
            setCurrentReading("");
            setReason("");
            onClose();
          },
        }
      );
    }
  };

  const handleClose = () => {
    setCurrentReading("");
    setReason("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md rounded-xl p-6">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">Adjust Reading</DialogTitle>
        </DialogHeader>

        <div className="mt-4 space-y-4">
          <div>
            <label
              htmlFor="adjustedReading"
              className="text-sm font-medium block mb-1"
            >
              Adjusted Reading
            </label>
            <input
              id="adjustedReading"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              className="w-full h-10 px-3 border border-input rounded-md focus:outline-none focus:ring-1 focus:ring-ring focus:border-ring transition-colors bg-background text-sm"
              value={currentReading}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d*\.?\d*$/.test(val)) {
                  setCurrentReading(val);
                }
              }}
              placeholder="Enter adjusted reading value"
            />
          </div>

          <div>
            <label
              htmlFor="reason"
              className="text-sm font-medium block mb-1"
            >
              Reason
            </label>
            <Textarea
              id="reason"
              placeholder="reason for adjustment"
              className="min-h-[80px]"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleAdjust}
            disabled={currentReading === ""}
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white"
          >
            Adjust
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdjustReadingDialog;

/* Legacy implementation:
interface AdjustReadingDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const LegacyAdjustReadingDialog: React.FC<AdjustReadingDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"manual" | "estimation">("manual");

  const { meterRunId, readingId, goToNextReading, validatorId } =
    useValidationContext();
  const remoteUtilityId = useRemoteUtilityId();

  // Hook call
  const { data: detail } = useMeterReadingDetail({
    remoteUtilityId,
    meterRunId,
    meterReadingId: readingId,
    validatorId,
  });

  const [consumption, setConsumption] = useState<number | undefined>();
  // keep currentReading as string for better input control
  const [currentReading, setCurrentReading] = useState<string>("");

  useEffect(() => {
    if (detail?.readingData) {
      setConsumption(detail.readingData.consumption);
      // ✅ prefill with reading OR empty string if null/undefined
      setCurrentReading(
        detail.readingData.currentReading !== null &&
        detail.readingData.currentReading !== undefined
          ? String(detail.readingData.currentReading)
          : ""
      );
    }
  }, [detail]);

  const previousReading = detail?.readingData?.previousReading;

  const adjustReading = usePutAdjustReadings(readingId, meterRunId);

  const handleAdjust = () => {
    // ✅ only trigger if there’s a number entered
    if (currentReading !== "") {
      adjustReading.mutate(
        {
          meter_reading: Number(currentReading), // convert safely here
          rule_status: 1,
        },
        {
          onSuccess: () => {
toast({
  variant: "default",
  title: "Success",
  description: "Reading adjusted successfully",
});
            logEvent('MX Reading adjusted manually', {
              previousReading,
              currentReading
            })
            goToNextReading();
            setCurrentReading("");
            onClose();
          },
        }
      );
    }
  };

  // ✅ calculate consumption value from number only
  const numericReading = currentReading === "" ? undefined : Number(currentReading);
  const consumptionValue =
    numericReading !== undefined
      ? numericReading - (previousReading ?? 0)
      : undefined;

  const hasError =
    consumptionValue !== undefined && consumptionValue < 0;

  const handleApplyEstimation = () => {};

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md animate-scale-in">
        <DialogHeader>
          <DialogTitle>Adjust Reading</DialogTitle>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-gray-600 mb-4">
            Enter the adjusted reading value or apply an estimation rule.
          </p>

          <div className="border-b mb-4">
            <div className="flex">
              <button
                className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
                  activeTab === "manual"
                    ? "border-blue-500 text-gray-900"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
                onClick={() => setActiveTab("manual")}
              >
                Manual Adjustment
              </button>
            </div>
          </div>

          {activeTab === "manual" && (
            <div className="animate-slide-in">
              <div className="mb-4">
                <label
                  htmlFor="reading-value"
                  className="block mb-2 text-sm font-medium"
                >
                  Reading Value
                </label>
                <input
                  id="reading-value"
                  type="text" // ✅ keep text for controlled filtering
                  inputMode="numeric" // ✅ mobile keyboard shows numbers
                  pattern="[0-9]*"
                  className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  value={currentReading}
                  // ✅ only allow digits (no letters or symbols)
                  onChange={(e) => {
                    const val = e.target.value;
                   // ✅ allow digits and optional decimal part
                    if (/^\d*\.?\d*$/.test(val)) {
                           setCurrentReading(val);
                          }
                  }}
                  placeholder="Enter new reading value"
                />
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-md text-sm">
                <div className="mb-2">
                  Previous Reading: {previousReading}
                </div>
                <div>
                  Consumption will be:{" "}
                  {consumptionValue !== undefined
                    ? consumptionValue
                    : consumption}
                </div>
              </div>
              {hasError && (
                <div className="text-sm text-red-600 mt-1">
                  Consumption cannot be negative. Please check the reading value.
                </div>
              )}
            </div>
          )}

          {activeTab === "estimation" && (
            <div className="animate-in">
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium">
                  Select Estimation Rule
                </label>

                <div
                  className="space-y-3 disabled:opacity-10 disabled:cursor-not-allowed"
                  aria-disabled={true}
                >
                  <label className="flex items-center p-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="estimation-rule"
                      value="average"
                      className="mr-2"
                    />
                    <div>
                      <div className="font-medium">Average Consumption</div>
                      <div className="text-xs text-gray-500">
                        Estimate based on average consumption of last 3 months
                      </div>
                    </div>
                  </label>

                  <label className="flex items-center p-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="estimation-rule"
                      value="same-month"
                      className="mr-2"
                    />
                    <div>
                      <div className="font-medium">Same Month Last Year</div>
                      <div className="text-xs text-gray-500">
                        Estimate based on consumption from same month last year
                      </div>
                    </div>
                  </label>

                  <label className="flex items-center p-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="estimation-rule"
                      value="last-month"
                      className="mr-2"
                    />
                    <div>
                      <div className="font-medium">Last Month Consumption</div>
                      <div className="text-xs text-gray-500">
                        Use previous month consumption as estimate
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-end">
          <Button variant="outline" onClick={onClose} className="mr-2">
            Cancel
          </Button>

          {activeTab === "manual" && (
            <Button
              onClick={handleAdjust}
              disabled={currentReading === "" || hasError}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              Adjust
            </Button>
          )}

          {activeTab === "estimation" && (
            <Button
              onClick={handleApplyEstimation}
              disabled={true}
              className="bg-purple-500 hover:bg-purple-600 disabled:opacity-50"
            >
              Apply Estimation
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
*/
